import {useCallback, useEffect, useState} from 'react';
import {Button} from '~library/atoms/Button';
import {MinusIcon} from '~library/svg/MinusIcon';
import {PlusIcon} from '~library/svg/PlusIcon';
import './Range.sass';

const calculatePercentage = (value, min, max) => {
  const percent = ((value - min) * 100) / (max - min);
  return `${percent}% 100%`;
};

export const Range = ({
  min = 0,
  max = 100,
  step = 1,
  onChange,
  value = 1,
  suffix,
  disabled = false,
  dataQaId,
  hasControls = false,
  onLocalChange,
  inputEditable = false,
  hiddenInput = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [backgroundSize, setBackgroundSize] = useState(calculatePercentage(value, min, max));

  const calculateValue = useCallback(
    (v = value) => {
      if (Number.isNaN(v)) {
        return v;
      }
      if (Number(v) < min) {
        return min;
      }
      if (Number(v) > max) {
        return max;
      }

      return Number(v);
    },
    [min, max, value]
  );

  const isInvalid = (v = value) => !!(Number.isNaN(v) || Number(v) < min || Number(v) > max);

  const parseNumber = useCallback(
    (v = value) => {
      const number = calculateValue(v);
      return Number.isNaN(number) ? 0 : number;
    },
    [calculateValue, value]
  );

  const onChangeHandler = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const onLocalChangeHandler = useCallback(
    (e) => {
      const targetValue = e.target.value;
      setLocalValue(targetValue);
      onLocalChange(parseNumber(targetValue));
      setBackgroundSize(calculatePercentage(parseNumber(targetValue), min, max));
    },
    [onLocalChange, min, max, parseNumber]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setBackgroundSize(calculatePercentage(value, min, max));
    setLocalValue(value);
  }, [value]);

  return (
    <div className="range">
      <div className="range-slider">
        {hasControls && (
          <Button
            data-qa-id="range-decrease"
            variant="transparent"
            className="range-control range-control-dec"
            onClick={() => {
              const newValue = Math.max(min, parseFloat(localValue) - parseFloat(step));
              setLocalValue(newValue);
              onLocalChange(newValue);
              setBackgroundSize(calculatePercentage(newValue, min, max));
            }}
          >
            <MinusIcon ratio={0.5} />
          </Button>
        )}
        <input
          type="range"
          min={min}
          max={max}
          value={parseNumber(localValue)}
          onChange={onChangeHandler}
          step={step}
          data-qa-id={dataQaId}
          style={{backgroundSize}}
          className="range__range-input"
          disabled={disabled}
        />
        {hasControls && (
          <Button
            data-qa-id="range-increase"
            variant="transparent"
            className="range-control range-control-inc"
            disabled={disabled}
            onClick={() => {
              const newValue = Math.min(parseFloat(localValue) + parseFloat(step), max);
              if (newValue !== localValue) {
                setLocalValue(newValue);
                onLocalChange(newValue);
                setBackgroundSize(calculatePercentage(newValue, min, max));
              }
            }}
          >
            <PlusIcon ratio={0.5} />
          </Button>
        )}
      </div>
      {!hiddenInput && (
        <div
          className={`range__value ${
            !disabled && isInvalid(localValue) ? 'range__value--invalid' : ''
          }`}
        >
          <input
            type="number"
            inputMode="numeric"
            value={localValue}
            style={{width: `${localValue?.toString().length}ch`}}
            onChange={inputEditable ? onLocalChangeHandler : undefined}
            disabled={!inputEditable}
            data-qa-id={`${dataQaId}-input`}
            className="range__value__input"
          />
          <span className={!inputEditable ? 'range__disabled-suffix' : ''}>{suffix && suffix}</span>
        </div>
      )}
    </div>
  );
};
